
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { AiFillCloseCircle } from "react-icons/ai";
import { PiSmileyFill } from "react-icons/pi";
import Picker from "@emoji-mart/react";
import { updatePost } from "../api";
import ToastMessage from "./ToastMessage";

const EditPost = ({ isOpen, isClose, currentPost }) => {
  const [editText, setEditText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const emojiRef = useRef(null);

  const hasCaption =
    typeof currentPost?.caption === "string" &&
    currentPost.caption.trim().length > 0;

  const isTextPost = currentPost?.type === "text";

  const canEdit = hasCaption || isTextPost;

  const editingField = hasCaption ? "caption" : "content";

  useEffect(() => {
    if (isOpen) {
      setEditText(
        hasCaption
          ? currentPost.caption
          : isTextPost
          ? currentPost?.content || ""
          : ""
      );

      setShowEmoji(false);
      setError("");
    }
  }, [isOpen, currentPost, hasCaption, isTextPost]);

  const addEmoji = (emoji) => {
    const codes = emoji.unified
      .split("-")
      .map((code) => parseInt(code, 16));

    setEditText((prev) => prev + String.fromCodePoint(...codes));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentPost?.id || !canEdit || saving) return;

    const value = editText.trim();

    if (!value) {
      setError("This field cannot be empty.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const formData = new FormData();

      formData.append("id", currentPost.id);
      formData.append(editingField, value);

      await updatePost(formData);

      ToastMessage("Your post has been updated!");

      setShowEmoji(false);
      isClose(false);
    } catch (err) {
      console.error("Update post error:", err);

      setError(
        err.response?.data?.message ||
        "Failed to update your post. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        emojiRef.current &&
        !emojiRef.current.contains(e.target)
      ) {
        setShowEmoji(false);
      }
    };

    if (showEmoji) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmoji]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          if (!saving) isClose(false);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-out duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative flex max-h-[90vh] w-full max-w-lg flex-col gap-5 rounded-2xl border border-purple-100 bg-white p-6 text-left shadow-xl transition-all">

                <button
                  type="button"
                  disabled={saving}
                  onClick={() => isClose(false)}
                  className="absolute right-3 top-3 z-10 rounded-full bg-purple-50 p-2 text-gray-500 transition hover:bg-purple-100 hover:text-purple-600 disabled:opacity-50"
                  aria-label="Close"
                >
                  <AiFillCloseCircle size={20} />
                </button>

                <div className="pr-10">
                  <Dialog.Title className="text-xl font-bold text-gray-900">
                    Edit Post
                  </Dialog.Title>

                  <p className="mt-1 text-sm text-gray-500">
                    {hasCaption
                      ? "Update your post caption."
                      : isTextPost
                      ? "Update your text post."
                      : "Editing is unavailable for this post."}
                  </p>
                </div>

                {!canEdit ? (
                  <div className="space-y-5">
                    <div className="rounded-xl border border-purple-100 bg-purple-50 p-5">
                      <p className="text-sm font-semibold text-gray-800">
                        No caption available
                      </p>

                      <p className="mt-2 text-sm leading-6 text-gray-600">
                        This post doesn't have a caption to edit.
                        Photos, videos, and voice recordings cannot
                        be edited.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => isClose(false)}
                      className="w-full rounded-xl bg-purple-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-purple-700"
                    >
                      Got it
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="w-full space-y-5">
                    <div>
                      <label
                        htmlFor="edit-post-text"
                        className="mb-2 block text-sm font-semibold text-gray-700"
                      >
                        {hasCaption ? "Post caption" : "Post content"}
                      </label>

                      <div className="relative rounded-xl border border-gray-200 bg-white focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100">
                        <textarea
                          id="edit-post-text"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={7}
                          className="w-full resize-none rounded-xl bg-transparent p-4 pb-12 text-sm font-medium text-gray-900 outline-none"
                          placeholder={
                            hasCaption
                              ? "Edit your caption..."
                              : "Edit your text..."
                          }
                          required
                        />

                        <button
                          type="button"
                          onClick={() => setShowEmoji((prev) => !prev)}
                          className="absolute bottom-3 right-3 rounded-lg p-1.5 text-gray-400 hover:bg-purple-50 hover:text-purple-600"
                          aria-label="Add emoji"
                        >
                          <PiSmileyFill size={22} />
                        </button>
                      </div>

                      {showEmoji && (
                        <div
                          ref={emojiRef}
                          className="relative z-20 mt-2 flex justify-end"
                        >
                          <Picker
                            emojiSize={20}
                            emojiButtonSize={28}
                            onEmojiSelect={addEmoji}
                            maxFrequentRows={4}
                            searchPosition="none"
                            perLine={9}
                          />
                        </div>
                      )}
                    </div>

                    {error && (
                      <p role="alert" className="text-sm text-red-600">
                        {error}
                      </p>
                    )}

                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => isClose(false)}
                        className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>

                      <button
                        type="submit"
                        disabled={saving || !editText.trim()}
                        className="rounded-xl bg-purple-600 px-6 py-3 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditPost;
